import React, { useState, useCallback, useEffect, useRef } from 'react';

const QuestionStepper = ({ questions, onComplete, disabled, onboardingMode }) => {
  // answers[i] = string[] of selected choices, or null if skipped
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState(() => questions.map(() => []));
  const [otherTexts, setOtherTexts] = useState(() => questions.map(() => ''));
  const [otherActive, setOtherActive] = useState(() => questions.map(() => false));
  const containerRef = useRef(null);
  const otherInputRef = useRef(null);

  const current = questions[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === questions.length - 1;
  const totalQuestions = questions.length;
  const currentSelections = answers[currentIndex] || [];
  const isOtherActive = otherActive[currentIndex];
  const currentOtherText = otherTexts[currentIndex] || '';
  const hasSelection = currentSelections.length > 0 || (isOtherActive && currentOtherText.trim().length > 0);

  // Determine if current question is single-select (auto-advance on tap)
  const isSingleSelect = onboardingMode && (current.selectionType === 'single' || !current.selectionType);

  // Focus the "Other" input when it becomes active
  useEffect(() => {
    if (isOtherActive && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, [isOtherActive]);

  const buildFinalAnswers = useCallback((overrideIndex, overrideValue) => {
    return answers.map((a, i) => {
      if (i === overrideIndex && overrideValue !== undefined) {
        return overrideValue;
      }
      const isOther = otherActive[i];
      const otherVal = otherTexts[i]?.trim();
      const selections = a && a.length > 0 ? [...a] : [];
      if (isOther && otherVal) {
        selections.push(otherVal);
      }
      if (selections.length === 0) return null;
      return selections.length === 1 ? selections[0] : selections;
    });
  }, [answers, otherActive, otherTexts]);

  const toggleChoice = useCallback((choice) => {
    // Deactivate "Other" when picking a preset choice in single-select mode
    if (isSingleSelect) {
      setOtherActive(prev => {
        const updated = [...prev];
        updated[currentIndex] = false;
        return updated;
      });
      setAnswers(prev => {
        const updated = [...prev];
        updated[currentIndex] = [choice];
        return updated;
      });
      // Auto-advance after brief visual feedback
      setTimeout(() => {
        if (isLast) {
          const finalAnswers = buildFinalAnswers(currentIndex, choice);
          onComplete(finalAnswers);
        } else {
          setCurrentIndex(prev => prev + 1);
        }
      }, 250);
      return;
    }

    // Multi-select: deactivate "Other" text if selecting a regular choice
    setOtherActive(prev => {
      const updated = [...prev];
      updated[currentIndex] = false;
      return updated;
    });

    setAnswers(prev => {
      const updated = [...prev];
      const current = updated[currentIndex] || [];
      if (current.includes(choice)) {
        updated[currentIndex] = current.filter(c => c !== choice);
      } else {
        updated[currentIndex] = [...current, choice];
      }
      return updated;
    });
  }, [currentIndex, isSingleSelect, isLast, buildFinalAnswers, onComplete]);

  const activateOther = useCallback(() => {
    if (isSingleSelect) {
      // In single-select, clear preset selections when activating Other
      setAnswers(prev => {
        const updated = [...prev];
        updated[currentIndex] = [];
        return updated;
      });
    }
    setOtherActive(prev => {
      const updated = [...prev];
      updated[currentIndex] = true;
      return updated;
    });
  }, [currentIndex, isSingleSelect]);

  const handleOtherTextChange = useCallback((e) => {
    const value = e.target.value;
    setOtherTexts(prev => {
      const updated = [...prev];
      updated[currentIndex] = value;
      return updated;
    });
  }, [currentIndex]);

  const handleOtherKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && currentOtherText.trim()) {
      e.preventDefault();
      if (isSingleSelect) {
        if (isLast) {
          const finalAnswers = buildFinalAnswers(currentIndex, currentOtherText.trim());
          onComplete(finalAnswers);
        } else {
          setCurrentIndex(prev => prev + 1);
        }
      } else {
        // For multi-select, Enter confirms and advances
        if (isLast) {
          onComplete(buildFinalAnswers());
        } else {
          setCurrentIndex(prev => prev + 1);
        }
      }
    }
  }, [currentOtherText, isSingleSelect, isLast, currentIndex, buildFinalAnswers, onComplete]);

  const confirmAndAdvance = useCallback(() => {
    if (!hasSelection) return;
    if (isLast) {
      onComplete(buildFinalAnswers());
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [hasSelection, isLast, buildFinalAnswers, onComplete]);

  const goBack = useCallback(() => {
    if (!isFirst) setCurrentIndex(prev => prev - 1);
  }, [isFirst]);

  const skip = useCallback(() => {
    setAnswers(prev => {
      const updated = [...prev];
      updated[currentIndex] = [];
      return updated;
    });
    setOtherActive(prev => {
      const updated = [...prev];
      updated[currentIndex] = false;
      return updated;
    });
    setOtherTexts(prev => {
      const updated = [...prev];
      updated[currentIndex] = '';
      return updated;
    });

    if (isLast) {
      const finalAnswers = answers.map((a, i) => {
        if (i === currentIndex) return null; // this one is being skipped
        const isOther = otherActive[i];
        const otherVal = otherTexts[i]?.trim();
        const selections = a && a.length > 0 ? [...a] : [];
        if (isOther && otherVal) selections.push(otherVal);
        if (selections.length === 0) return null;
        return selections.length === 1 ? selections[0] : selections;
      });
      onComplete(finalAnswers);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [answers, currentIndex, isLast, onComplete, otherActive, otherTexts]);

  // Keyboard navigation: left/right arrows, Enter to confirm
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (disabled) return;
      // Don't intercept keys when typing in the Other input
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowLeft' && !isFirst) {
        e.preventDefault();
        goBack();
      } else if (e.key === 'Enter' && hasSelection) {
        e.preventDefault();
        confirmAndAdvance();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }
  }, [disabled, isFirst, hasSelection, goBack, confirmAndAdvance]);

  // Auto-focus container for keyboard support
  useEffect(() => {
    if (!otherActive[currentIndex]) {
      containerRef.current?.focus();
    }
  }, [currentIndex, otherActive]);

  if (!questions || questions.length === 0) return null;

  return (
    <>
      <style>{hoverStyles}</style>
      <div
        ref={containerRef}
        style={styles.container}
        tabIndex={-1}
        data-testid="question-stepper"
      >
        {totalQuestions > 1 && (
          <div style={styles.progressRow}>
            {questions.map((_, idx) => (
              <div
                key={idx}
                data-testid="progress-dot"
                style={{
                  ...styles.progressDot,
                  ...(idx === currentIndex ? styles.progressDotActive : {}),
                  ...(answers[idx] && answers[idx].length > 0 && idx !== currentIndex ? styles.progressDotAnswered : {}),
                }}
              />
            ))}
          </div>
        )}

        <div style={styles.questionText}>
          {current.question}
          {onboardingMode && current.selectionType === 'multi' && (
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-400)', fontStyle: 'italic', marginLeft: 6 }}>
              Select all that apply
            </span>
          )}
        </div>

        <div style={styles.choicesRow}>
          {current.choices.map((choice, idx) => {
            const isSelected = currentSelections.includes(choice);
            return (
              <button
                key={idx}
                onClick={() => toggleChoice(choice)}
                disabled={disabled}
                className="qs-choice-btn"
                style={{
                  ...styles.choiceButton,
                  ...(isSelected ? styles.choiceButtonSelected : {}),
                  ...(disabled ? styles.choiceButtonDisabled : {}),
                }}
              >
                {choice}
              </button>
            );
          })}

          {/* "Other" freetext option */}
          {current.allowOther && !isOtherActive && (
            <button
              onClick={activateOther}
              disabled={disabled}
              className="qs-choice-btn"
              style={{
                ...styles.choiceButton,
                ...(disabled ? styles.choiceButtonDisabled : {}),
                fontStyle: 'italic',
              }}
            >
              Other...
            </button>
          )}
        </div>

        {/* Freetext input for "Other" */}
        {current.allowOther && isOtherActive && (
          <div style={styles.otherRow}>
            <input
              ref={otherInputRef}
              type="text"
              value={currentOtherText}
              onChange={handleOtherTextChange}
              onKeyDown={handleOtherKeyDown}
              placeholder="Type your answer..."
              disabled={disabled}
              style={styles.otherInput}
              data-testid="other-input"
            />
            <button
              onClick={() => {
                setOtherActive(prev => {
                  const updated = [...prev];
                  updated[currentIndex] = false;
                  return updated;
                });
                setOtherTexts(prev => {
                  const updated = [...prev];
                  updated[currentIndex] = '';
                  return updated;
                });
              }}
              className="qs-nav-btn"
              style={styles.otherCancel}
            >
              Cancel
            </button>
          </div>
        )}

        <div style={styles.navRow}>
          <button
            onClick={goBack}
            disabled={isFirst || disabled}
            className="qs-nav-btn"
            style={{
              ...styles.navButton,
              ...(isFirst ? styles.navButtonDisabled : {}),
            }}
            aria-label="Previous question"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>

          <button
            onClick={skip}
            disabled={disabled}
            className="qs-nav-btn"
            style={styles.skipButton}
          >
            Skip
          </button>

          <button
            onClick={confirmAndAdvance}
            disabled={!hasSelection || disabled}
            className="qs-confirm-btn"
            style={{
              ...styles.confirmButton,
              ...(!hasSelection || disabled ? styles.confirmButtonDisabled : {}),
            }}
          >
            {isLast ? 'Done' : 'Next'}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

const hoverStyles = `
  .qs-choice-btn:hover:not(:disabled) {
    background-color: var(--primary-50) !important;
    border-color: var(--primary-500) !important;
    transform: translateY(-1px);
  }
  .qs-choice-btn:active:not(:disabled) {
    transform: translateY(0);
    background-color: var(--primary-100) !important;
  }
  .qs-nav-btn:hover:not(:disabled) {
    color: var(--gray-700) !important;
  }
  .qs-confirm-btn:hover:not(:disabled) {
    background: var(--primary-700) !important;
  }
`;

const styles = {
  container: {
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    outline: 'none',
  },
  progressRow: {
    display: 'flex',
    gap: '6px',
    justifyContent: 'center',
  },
  progressDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--gray-200)',
    transition: 'all 0.2s ease',
  },
  progressDotActive: {
    backgroundColor: 'var(--primary-500)',
    transform: 'scale(1.25)',
  },
  progressDotAnswered: {
    backgroundColor: 'var(--primary-300)',
  },
  questionText: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--gray-700)',
  },
  choicesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  choiceButton: {
    padding: '8px 16px',
    borderRadius: 'var(--radius-md, 0.5rem)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'var(--primary-400)',
    backgroundColor: 'white',
    color: 'var(--primary-600)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },
  choiceButtonSelected: {
    backgroundColor: 'var(--primary-500)',
    color: 'white',
    borderColor: 'var(--primary-500)',
  },
  choiceButtonDisabled: {
    opacity: 0.5,
    cursor: 'default',
    borderColor: 'var(--gray-300)',
    color: 'var(--gray-400)',
  },
  otherRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  otherInput: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: 'var(--radius-md, 0.5rem)',
    border: '1px solid var(--primary-400)',
    fontSize: '14px',
    color: 'var(--gray-700)',
    outline: 'none',
  },
  otherCancel: {
    padding: '4px 10px',
    borderRadius: 'var(--radius-sm, 0.375rem)',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--gray-400)',
    fontSize: '13px',
    cursor: 'pointer',
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: 'var(--radius-sm, 0.375rem)',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--gray-500)',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'color 0.15s ease',
  },
  navButtonDisabled: {
    opacity: 0.3,
    cursor: 'default',
  },
  skipButton: {
    marginLeft: 'auto',
    padding: '4px 10px',
    borderRadius: 'var(--radius-sm, 0.375rem)',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--gray-400)',
    fontSize: '13px',
    cursor: 'pointer',
  },
  confirmButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 14px',
    borderRadius: 'var(--radius-sm, 0.375rem)',
    border: 'none',
    backgroundColor: 'var(--primary-600)',
    color: 'white',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  confirmButtonDisabled: {
    opacity: 0.4,
    cursor: 'default',
  },
};

export default QuestionStepper;
